/**
 * src/pages/admin/AdminRatings.jsx
 *
 * Founder moderation queue for Scrimly Ratings (features/club-ratings,
 * Wireframe screens 11 and 12). Tabs: pending comments, open reports, club
 * suggestions, flagged clubs.
 *
 * Reads and writes only through the founder-gated callables
 * (ratingsAdminQueue, ratingsAdminModerate, ratingsAdminClubs): every
 * ratings collection is deny-all in firestore.rules. The callables never
 * return phone numbers, account IDs, author keys or per-rating age groups
 * (Test plan T-05, T-10), so this page can't show them.
 *
 * English only (admin path, BA §6).
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { httpsCallable, getFunctions } from 'firebase/functions';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import { ArrowPathIcon, ExclamationTriangleIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';

const fns = () => getFunctions(undefined, 'us-central1');
const call = async (name, data) => (await httpsCallable(fns(), name)(data || {})).data;

const TABS = [
  { key: 'comments', label: 'Comments' },
  { key: 'reports', label: 'Reports' },
  { key: 'clubs', label: 'Club suggestions' },
  { key: 'flags', label: 'Flags' },
];

const REJECT_REASONS = [
  ['names_person', 'Names a person'],
  ['too_specific', 'Too specific, could identify someone'],
  ['accusation', 'Accusation, not experience'],
  ['off_topic', 'Not about this club'],
  ['spam', 'Spam'],
];

const REPORT_REASONS = {
  identifies_someone: 'Names or identifies someone',
  false_accusation: 'False or unfair accusation',
  safety: 'Safety concern',
  spam: 'Spam or not about this club',
  other: 'Something else',
};

const FLAG_LABELS = {
  name: 'name', date: 'date', incident: 'incident', child_detail: 'child detail', contact: 'contact',
};

const COUNTIES = ['riverside', 'san-bernardino', 'los-angeles', 'orange', 'san-diego', 'ventura'];

function slugify(name) {
  return String(name || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
    .toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 80);
}

export default function AdminRatings() {
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  const [tab, setTab] = useState('comments');
  const [queue, setQueue] = useState(null);
  const [activeClubs, setActiveClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [q, dir] = await Promise.all([
        call('ratingsAdminQueue'),
        call('ratingsAdminClubs', { action: 'list' }),
      ]);
      setQueue(q);
      setActiveClubs((dir.clubs || []).filter((c) => c.status === 'active'));
    } catch (e) {
      setError(e?.message || 'Could not load the queue.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (!blocked) load(); }, [blocked, load]);

  async function act(id, name, data, confirmText) {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusyId(id);
    setError(null);
    try {
      await call(name, data);
      await load();
    } catch (e) {
      setError(e?.message || 'That action failed.');
    } finally {
      setBusyId(null);
    }
  }

  if (blocked) {
    return <div className="p-6 text-sm text-zinc-600">Founder access only.</div>;
  }

  const counts = {
    comments: queue?.comments?.length || 0,
    reports: queue?.reports?.length || 0,
    clubs: queue?.clubs?.length || 0,
    flags: (queue?.flags?.length || 0) + (queue?.manyClubs?.length || 0),
  };

  return (
    <div className="max-w-4xl mx-auto p-4 sm:p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Ratings moderation</h1>
          <p className="text-sm text-zinc-500">
            Scrimly Ratings · <Link to="/admin/ratings/clubs" className="text-lime-700 hover:underline">Club directory</Link>
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-2 rounded-lg hover:bg-zinc-100 disabled:opacity-40"
          aria-label="Refresh"
        >
          <ArrowPathIcon className={`w-5 h-5 text-zinc-500 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium border ${
              tab === t.key ? 'bg-zinc-900 text-white border-zinc-900' : 'bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50'
            }`}
          >
            {t.label}
            <span className={`ml-1.5 text-xs ${counts[t.key] ? 'font-bold' : 'opacity-60'}`}>{counts[t.key]}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationTriangleIcon className="w-5 h-5 flex-none" />
          <span>{error}</span>
        </div>
      )}

      {loading && !queue && (
        <div className="rounded-lg border border-zinc-200 p-6 text-sm text-zinc-500">Loading…</div>
      )}

      {queue && tab === 'comments' && (
        <Section empty={counts.comments === 0}>
          {queue.comments.map((c) => (
            <CommentReview key={c.publicId} item={c} busy={busyId === c.publicId} onAct={act} />
          ))}
        </Section>
      )}

      {queue && tab === 'reports' && (
        <Section empty={counts.reports === 0}>
          {queue.reports.map((r) => (
            <div key={r.reportId} className="p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-zinc-900">{r.clubName}</span>
                <span className={`text-xs px-2 py-0.5 rounded border ${r.reason === 'safety' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-zinc-50 border-zinc-200 text-zinc-600'}`}>
                  {REPORT_REASONS[r.reason] || r.reason}
                </span>
                {r.daysInQueue !== null && <span className="text-xs text-zinc-400">{r.daysInQueue}d in queue</span>}
              </div>
              <p className="mt-2 text-sm text-zinc-800 whitespace-pre-wrap">
                {r.commentText || <em className="text-zinc-400">No comment text (stars only or already removed)</em>}
              </p>
              {r.details && <p className="mt-1 text-xs text-zinc-500">Reporter: {r.details}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                <Btn busy={busyId === r.reportId} onClick={() => act(r.reportId, 'ratingsAdminModerate', { action: 'resolve_report', reportId: r.reportId, uphold: true })}>
                  Uphold: remove comment
                </Btn>
                <Btn tone="danger" busy={busyId === r.reportId} onClick={() => act(r.reportId, 'ratingsAdminModerate', { action: 'resolve_report', reportId: r.reportId, uphold: true, removeRating: true }, 'Remove the whole rating, including its stars?')}>
                  Uphold: remove rating
                </Btn>
                <Btn tone="ghost" busy={busyId === r.reportId} onClick={() => act(r.reportId, 'ratingsAdminModerate', { action: 'resolve_report', reportId: r.reportId, uphold: false })}>
                  Dismiss
                </Btn>
              </div>
            </div>
          ))}
        </Section>
      )}

      {queue && tab === 'clubs' && (
        <Section empty={counts.clubs === 0}>
          {queue.clubs.map((c) => (
            <SuggestionReview key={c.clubId} item={c} activeClubs={activeClubs} busy={busyId === c.clubId} onAct={act} />
          ))}
        </Section>
      )}

      {queue && tab === 'flags' && (
        <Section empty={counts.flags === 0}>
          {/* Many-clubs holds (features/rating-safeguards/, Wireframe screen 2).
              One card per account; the account itself is never shown. */}
          {(queue.manyClubs || []).map((g, i) => {
            const ids = g.held.map((h) => h.publicId);
            const key = `many-${i}-${ids[0]}`;
            return (
              <div key={key} className="p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-semibold text-zinc-900">An account rated {g.clubCount} clubs in {g.season}</span>
                  <span className="text-xs px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">Many clubs</span>
                </div>
                <p className="mt-2 text-xs text-zinc-500">{g.clubNames.join(' · ')}</p>
                <p className="mt-1 text-xs text-zinc-700">Held: {g.held.map((h) => h.clubName).join(', ')}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Btn busy={busyId === key} onClick={() => act(key, 'ratingsAdminModerate', { action: 'release_held', publicIds: ids }, 'Release the held ratings? They will publish on the normal schedule.')}>
                    Release held
                  </Btn>
                  <Btn tone="danger" busy={busyId === key} onClick={() => act(key, 'ratingsAdminModerate', { action: 'remove_held', publicIds: ids }, 'Remove the held ratings? This cannot be undone.')}>
                    Remove held
                  </Btn>
                </div>
              </div>
            );
          })}
          {queue.flags.map((f) => (
            <div key={f.clubId} className="p-4">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="font-semibold text-zinc-900">{f.clubName}</span>
                <span className="text-xs px-2 py-0.5 rounded border bg-amber-50 border-amber-200 text-amber-800">
                  {f.reason === 'new_accounts' ? 'Many brand-new accounts' : 'Unusual burst of ratings'}
                </span>
                <span className="text-xs text-zinc-500">{f.heldCount} held</span>
              </div>
              <p className="mt-2 text-xs text-zinc-500">
                Age groups (counts only): {Object.entries(f.ageGroups || {}).map(([a, n]) => `${a} ${n}`).join(', ') || 'none'}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Btn busy={busyId === f.clubId} onClick={() => act(f.clubId, 'ratingsAdminClubs', { action: 'release', clubId: f.clubId }, 'Release held ratings? They will publish on the normal schedule.')}>
                  Release
                </Btn>
                <Btn tone="ghost" busy={busyId === f.clubId} onClick={() => act(f.clubId, 'ratingsAdminClubs', { action: 'hold', clubId: f.clubId })}>
                  Keep holding (hold new ones too)
                </Btn>
              </div>
            </div>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ empty, children }) {
  return (
    <div className="rounded-xl border border-zinc-200 divide-y divide-zinc-100 bg-white">
      {empty ? <div className="p-4 text-sm text-zinc-500">Queue is clear.</div> : children}
    </div>
  );
}

function Btn({ children, onClick, busy, disabled, tone }) {
  const cls = tone === 'danger'
    ? 'bg-white border border-red-300 text-red-700 hover:bg-red-50'
    : tone === 'ghost'
      ? 'bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50'
      : 'bg-lime-400 text-zinc-900 hover:bg-lime-300';
  return (
    <button type="button" onClick={onClick} disabled={busy || disabled} className={`px-3 py-1.5 rounded-lg text-sm font-semibold disabled:opacity-40 ${cls}`}>
      {busy ? '…' : children}
    </button>
  );
}

function CommentReview({ item, busy, onAct }) {
  const [text, setText] = useState(item.text);
  const [reason, setReason] = useState('');
  const edited = text.trim() !== item.text.trim();
  const stars = item.stars ? Object.values(item.stars).join(' ') : '';

  return (
    <div className={`p-4 ${item.safety ? 'bg-red-50/50' : ''}`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {item.safety && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded border bg-red-100 border-red-300 text-red-800 font-semibold">
            <ShieldExclamationIcon className="w-4 h-4" /> Safety
          </span>
        )}
        <span className="font-semibold text-zinc-900">{item.clubName}</span>
        <span className="text-xs text-zinc-500">{item.season} · {String(item.lang).toUpperCase()}</span>
        <span className="text-xs text-zinc-500">Stars {stars} · {item.recommend ? 'Recommends' : "Doesn't recommend"}</span>
        {item.daysInQueue !== null && <span className="text-xs text-zinc-400">{item.daysInQueue}d in queue</span>}
      </div>
      <p className="mt-1 text-xs">
        {item.checkStatus === 'failed' && <span className="text-amber-700">Check failed (patterns only)</span>}
        {item.flags?.length > 0 && (
          <span className="text-amber-800">
            Flags: {item.flags.map((f) => `"${f.quote}" (${FLAG_LABELS[f.type] || f.type})`).join(', ')}
          </span>
        )}
        {item.checkStatus === 'ok' && !item.flags?.length && <span className="text-zinc-400">Check: nothing flagged</span>}
      </p>
      <label className="sr-only" htmlFor={`c-${item.publicId}`}>Comment text</label>
      <textarea
        id={`c-${item.publicId}`}
        rows={4}
        maxLength={1000}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="mt-2 w-full rounded-lg border border-zinc-300 p-2 text-sm"
      />
      {item.safety && (
        <p className="mt-1 text-xs text-red-700">
          Possible child safety issue. Do not publish. Follow the safety path in the guidelines (wording from attorney, D-12).
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {!edited ? (
          <Btn busy={busy} onClick={() => onAct(item.publicId, 'ratingsAdminModerate', { action: 'approve', publicId: item.publicId })}>
            Approve
          </Btn>
        ) : (
          <Btn busy={busy} onClick={() => onAct(item.publicId, 'ratingsAdminModerate', { action: 'approve_edit', publicId: item.publicId, text })}>
            Approve with my edits
          </Btn>
        )}
        <select
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="rounded-lg border border-zinc-300 px-2 py-1.5 text-sm"
          aria-label="Reject reason"
        >
          <option value="">Reject because…</option>
          {REJECT_REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Btn tone="danger" busy={busy} disabled={!reason} onClick={() => onAct(item.publicId, 'ratingsAdminModerate', { action: 'reject', publicId: item.publicId, reason })}>
          Reject
        </Btn>
      </div>
    </div>
  );
}

function SuggestionReview({ item, activeClubs, busy, onAct }) {
  const [slug, setSlug] = useState(slugify(item.name));
  const [county, setCounty] = useState('');
  const [duplicateOf, setDuplicateOf] = useState('');

  return (
    <div className="p-4">
      <div className="text-sm">
        <span className="font-semibold text-zinc-900">{item.name}</span>
        <span className="text-zinc-500"> · {item.city}</span>
        <span className="text-xs text-zinc-400"> · {item.heldRatings} rating(s) waiting</span>
      </div>
      {item.website && <p className="text-xs text-zinc-500 break-all">Website (as typed, not a link): {item.website}</p>}
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <label className="text-xs text-zinc-500">Slug
          <input value={slug} onChange={(e) => setSlug(e.target.value)} className="mt-1 w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-sm" />
        </label>
        <label className="text-xs text-zinc-500">County
          <select value={county} onChange={(e) => setCounty(e.target.value)} className="mt-1 w-full rounded-lg border border-zinc-300 px-2 py-1.5 text-sm">
            <option value="">Choose…</option>
            {COUNTIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <div className="flex items-end">
          <Btn busy={busy} disabled={!county || !slug} onClick={() => onAct(item.clubId, 'ratingsAdminClubs', { action: 'approve_suggestion', clubId: item.clubId, slug, county })}>
            Approve club
          </Btn>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="text-xs text-zinc-500">Duplicate of
          <select value={duplicateOf} onChange={(e) => setDuplicateOf(e.target.value)} className="mt-1 block rounded-lg border border-zinc-300 px-2 py-1.5 text-sm max-w-xs">
            <option value="">Choose a club…</option>
            {activeClubs.map((c) => <option key={c.clubId} value={c.clubId}>{c.name} · {c.city}</option>)}
          </select>
        </label>
        <Btn tone="ghost" busy={busy} disabled={!duplicateOf} onClick={() => onAct(item.clubId, 'ratingsAdminClubs', { action: 'reject_suggestion', clubId: item.clubId, intoClubId: duplicateOf }, 'Move its ratings into the selected club?')}>
          Merge as duplicate
        </Btn>
        <Btn tone="danger" busy={busy} onClick={() => onAct(item.clubId, 'ratingsAdminClubs', { action: 'reject_suggestion', clubId: item.clubId }, 'Reject this club and discard its waiting ratings?')}>
          Reject
        </Btn>
      </div>
    </div>
  );
}
